import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
} from "react";
import {
  generateQueryString,
  generateUniqueId,
  getQueryParams,
  getValidMode,
  incrementStringNumber,
  isWindowAvailable,
  postMessageToParent,
} from "../utils";
import { convAiClient } from "../API";
import {
  BOT_ERROR_MSG,
  BOT_ROLE,
  footerModalConfigTypes,
  CONSENT_TYPE,
  IFRAME_ANIMATION_CLOSE_DELAY,
  initalChatInputConfigs,
  initialFooterModalConfig,
  initialModalConfig,
  initialOptionSelection,
  VALID_MODES,
  ROUTES,
  CHAT_MODE,
  initialEditState,
} from "../constants/index.js";
import {
  ChatInputConfigs,
  IChatContext,
  IChatContextV2,
  IChatProviderProps,
  IFooterModalConfig,
  IModalConfig,
  Message,
  OptionSelectionConfig,
  ScheduleConfig,
} from "../types/ChatInterfaces";
import { Theme, useMediaQuery, useTheme } from "@mui/material";
import { handleApiResponse } from "../utils/apiUtils";
import cloneDeep from "lodash.clonedeep";
import { getLastSystemMsgId, removeMessageById } from "../utils/messageUtils";
import axios, { AxiosError } from "axios";
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from "../utils/localStorage";
import { useLogoList } from "../hooks/useLogoList";
import { generateStarterMsg } from "../utils/botSatrter";
const FETCH_CALL_TYPE = {
  initial: "initial",
  history: "history",
};
import {
  getParentUrlQuery,
  handeQueryParam,
  replaceUrlInHistory,
  updateChatbotUrl,
} from "../utils/urlUtils";
import { ResponseType } from "../types/apiTypes";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "./TranslationContext";
import { getShortLanguageCode } from "../utils/translation";

// Edit state interface
interface EditState {
  isEditing: boolean;
  originalMessage: Message | null;
  messagesAfterEdit: Message[];
  originalInputValue: { value: string; question_id: string };
  previousChatInputConfigs: ChatInputConfigs | null;
}

const V2ChatContext = createContext<IChatContextV2>({
  messages: [],
  config: null,
  error: null,
  activeView: "widget",
  botLoading: false,
  convId: "",
  modalConfig: initialModalConfig,
  optionSelection: initialOptionSelection,
  logoList: [],
  editState: initialEditState,
  showChat: () => {},
  hideChat: () => {},
  sendData: () => {},
  showWidgetButton: () => true,
  generatePayload: (data: any, allow_channel_shift: string, mode?: string) => ({
    metadata: { convId: "1", turn_id: 0 },
    request: { ...data.action },
    ...(mode ? { mode } : {}),
  }),
  genrateUserMsg: () => {},
  fetchBotResponse: async () => {},

  handleAddMessage: () => {},
  setMessages: () => {},
  setModalConfig: () => {},
  setConvId: () => {},
  setOptionSelection: () => {},
  schedulerMsg: {
    style: { height: "38.8rem" },
    data: {},
    scheduleConfirmation: {},
  },
  setSchedulerMsg: () => {},
  getTurnId: () => "1",
  handleFormSubmit: () => {},
  reinitializeChat: () => {
    throw new Error("reinitializeChat function is not implemented");
  },
  handleUpdateMessages: () => {},
  handleInterviewSchedule: () => {},
  handleInitialize: () => {},
  chatInputConfigs: initalChatInputConfigs,
  setChatInputConfigs: () => {},
  handleParentWebsiteEvent: () => {},
  isPopupOpen: false,
  setIsPopupOpen: () => {},
  isV2Chat: true,
  fetchLatestChat: () => {},
  initializeData: {},

  setInitializeData: () => {},
  footerModalConfig: initialFooterModalConfig,
  setFooterModalConfig: () => {},
  handleInterviewScheduleMsgId: (data?: any) => {},
  parentConvId: "",
  setBotLoading: () => {},
  independentStarterBot: false,
  latestResponseHeaders: null,
  handleVideoLeave: (interviewId: string, payload: any) => {},
  isIndependentBot: false,
  handleUpdateMessage: () => {},
  cancelEditMessage: () => {},
  setEditState: () => {},
});

const STATUS_ERROR_CODE = 422;
const STATUS_ERROR_404 = 404;
const STATUS_ERROR_500 = 500;
export const useChat = () => useContext(V2ChatContext);
export const V2ChatProvider: React.FC<IChatProviderProps> = ({
  config,
  updateConfig,
  isIndependentBot = false,
  independentStarterBot = false,
  children,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<string>("widget");
  const [botLoading, setBotLoading] = useState<boolean>(true);
  const [convId, setConvId] = useState<string>(
    isIndependentBot
      ? "" // For independent bots, convId will be synced from URL params
      : getLocalStorageItem({
          botId: config?.botId,
          filterId: config?.filterId,
          key: "convId",
        }) || ""
  );
  const [modalConfig, setModalConfig] =
    useState<IModalConfig>(initialModalConfig);
  const [optionSelection, setOptionSelection] = useState<OptionSelectionConfig>(
    initialOptionSelection
  );
  const [initializeData, setInitializeData] = useState<any>({});
  const [schedulerMsg, setSchedulerMsg] = useState<ScheduleConfig>({
    style: { height: "38.8rem" },
    data: {},
    scheduleConfirmation: {},
  });
  const [chatInputConfigs, setChatInputConfigs] = useState<ChatInputConfigs>({
    disableInput: false,
    placeholder: config?.placeholder || [],
    value: "",
  });
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(
    !(
      isWindowAvailable &&
      getLocalStorageItem({
        botId: config?.botId,
        filterId: config?.filterId,
        key: "isPopupOpen",
      }) === "false"
    )
  );
  const [footerModalConfig, setFooterModalConfig] =
    useState<IFooterModalConfig>(initialFooterModalConfig);
  const [latestResponseHeaders, setLatestResponseHeaders] = useState<any>(null);
  const [editState, setEditState] = useState<EditState>(initialEditState);
  const { logoList } = useLogoList(config?.agencyId);
  const lastSystemMsgId = getLastSystemMsgId(messages);
  const lastMsgRole = messages[messages?.length - 1]?.role;

  const parentQuery = getParentUrlQuery(initializeData?.parentUrl);
  const parentWebsiteConvId = parentQuery?.conversation_id;
  const {
    bot_id: IndependentChatBotId,
    conv_id: IndependentChatConvId,
    message_id: parentMessageId,
  } = useParams();
  const navigate = useNavigate();
  const parentConvId = IndependentChatConvId || parentWebsiteConvId;
  const isV2Chat = true;
  const theme = useTheme<Theme>();
  // @ts-ignore
  const onlyMobileScreen = useMediaQuery(theme.breakpoints.down("tablet"), {
    noSsr: true,
  });
  const abortControllerRef = useRef(new AbortController());

  const { t, handleLanguageChange, currentLanguage, initializeTranslations } =
    useTranslation();

  const showChat = () => {
    setActiveView("side");
    postMessageToParent("UI.SET-CLASS", [
      "joveo-widget-web",
      `joveo-widget-side`,
    ]);
    postMessageToParent("UI.OPENED", undefined);
  };

  const hideChat = () => {
    const widgetPopupOpen = !(
      isWindowAvailable &&
      getLocalStorageItem({
        botId: config?.botId,
        filterId: config?.filterId,
        key: "isPopupOpen",
      }) === "false"
    );

    setActiveView("widget");
    setModalConfig((prevState) => ({
      ...prevState,
      open: false,
      type: "",
    }));
    setTimeout(() => {
      postMessageToParent("UI.SET-CLASS", [
        "joveo-widget-web",
        `joveo-widget-${widgetPopupOpen ? "widget" : "popup-close"}`,
      ]);
      postMessageToParent("UI.CLOSED", undefined);
    }, IFRAME_ANIMATION_CLOSE_DELAY);
  };

  const getUniversalConvId = () => {
    if (parentConvId) {
      return parentConvId;
    }
    return (
      getLocalStorageItem({
        botId: config?.botId,
        filterId: config?.filterId,
        key: "convId",
      }) || ""
    );
  };
  const handleIndependentChat = async (urlData: any) => {
    if (!urlData?.conversation_id) return;
    // handleClearOldData();
    setConvId("");
    setMessages([]);
    setModalConfig(initialModalConfig);
    setOptionSelection(initialOptionSelection);
    //sending parent convId to history call, as initializeData is not available

    handleHistory({
      localConvId: urlData?.conversation_id,
      parentConvId: urlData?.conversation_id,
      parentMessageId: urlData?.message_id,
      localBotId: urlData?.botId,
      localInitializeData: urlData?.localInitializeData,
    });
  };
  const handleMailLink = async (urlData: any) => {
    if (!urlData?.conversation_id) return;
    // handleClearOldData();
    setConvId("");
    setMessages([]);
    setModalConfig(initialModalConfig);
    setOptionSelection(initialOptionSelection);
    //sending parent convId to history call, as initializeData is not available

    handleHistory({
      localConvId: urlData?.conversation_id,
      parentConvId: urlData?.conversation_id,
      localInitializeData: urlData?.localInitializeData,
    });
    showChat();
  };

  const hideChatbotVisibility = () => {
    setActiveView("hidden");
    setTimeout(() => {
      postMessageToParent("UI.SET-CLASS", [
        "joveo-widget-web",
        `joveo-widget-hidden`,
      ]);
    }, IFRAME_ANIMATION_CLOSE_DELAY + 100);
  };

  const sendData = (data: any) => {
    if (data?.payload?.type === "queryParam") {
      handeQueryParam(data?.payload?.data);
    }
  };

  const showWidgetButton = () => {
    return !config?.hideWidget;
  };

  useEffect(() => {
    setChatInputConfigs((prev) => ({
      ...prev,
      placeholder: config?.placeholder || [],
    }));
  }, [config?.placeholder]);

  const getTurnId = (incrementCount?: number) => {
    if (messages?.length) {
      return (
        incrementStringNumber(
          messages[messages.length - 1]?.turn_id,
          incrementCount
        ) ?? "1"
      );
    }
    return "1";
  };

  const generatePayload = (
    data: any,
    allow_channel_shift: string,
    mode?: string
  ) => {
    return {
      metadata: {},
      request: {
        // turn_id: getTurnId(),
        ...data.action,
        ...(allow_channel_shift ? { allow_channel_shift: false } : {}),
      },
      ...(mode ? { mode } : {}),
    };
  };

  const genrateUserMsg = (data: any) => {
    const type = data?.body?.type || ResponseType.Text;
    const val: Message = {
      role: "user",
      type: type,
      timestamp: new Date().toISOString(),
      id: generateUniqueId(),
      // turn_id: getTurnId(0),
      body: data?.body || {},
    };
    if (!data.body) return null;
    setMessages((previous) => [...previous, val]);
    return val;
  };
  const handleClearOldData = () => {
    removeLocalStorageItem({
      botId: config?.botId,
      filterId: config?.filterId,
      key: "convId",
    });
    removeLocalStorageItem({
      botId: config?.botId,
      filterId: config?.filterId,
      key: "preferred_language",
    });
    setConvId("");
    setMessages([]);
    setModalConfig(initialModalConfig);
    setOptionSelection(initialOptionSelection);
    setEditState(initialEditState);
  };
  const fetchBotResponse = async (data: any, overrideState?: any) => {
    let conversationId = convId;
    const botId = config?.botId;
    const newUserMsg = genrateUserMsg(data);

    if (independentStarterBot || !getUniversalConvId()) {
      const filterId = initializeData?.config?.filterId;
      const websiteUrl = initializeData?.parentUrl || "";
      const mode = getValidMode(initializeData?.config?.mode);
      await handleInitialConversation({
        body: {
          tnc_accepted: true,
          mode: mode,
          user_input: data?.body?.text || "",
          ...(filterId ? { filter_id: filterId } : {}),
          ...(currentLanguage
            ? { langauge: getShortLanguageCode(currentLanguage) }
            : {}),
        },
        headers: {
          website_url: websiteUrl,
        },
      });
      return;
    }
    abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    const parentQuery = getParentUrlQuery(initializeData?.parentUrl);
    const { allow_channel_shift } = parentQuery || {};
    const mode = getValidMode(initializeData?.config?.mode);
    const payload = generatePayload(data, allow_channel_shift, mode);

    setBotLoading(true);
    const isQueryApi = config?.isQueryApi ? `?show_demo_script=true` : "";
    const isPreview = config?.isPreview ? `/preview` : "";
    const isConversationId = conversationId ? `/${conversationId}` : "";
    const localLatestSystemMsgId = editState?.isEditing
      ? getLastSystemMsgId(editState?.messagesAfterEdit)
      : lastSystemMsgId;

    try {
      const response = await convAiClient.post(
        `/v1/bots/${botId}${isPreview}/conversations${isConversationId}${isQueryApi}`,
        payload,
        {
          headers: {
            latest_msg_id: localLatestSystemMsgId || "",
            website_url: initializeData?.parentUrl || "",
            js_version: initializeData?.js_version || undefined,
          },
          signal: abortControllerRef.current.signal,
        }
      );
      if (response?.status !== 200) {
        throw new Error("Network response was not ok");
      }
      const responseHeaders = response?.headers;
      setLatestResponseHeaders(responseHeaders);
      const metadata = response?.data?.metadata;
      const dataResp = response?.data?.response;
      if (allow_channel_shift) {
        const { parentUrl } = initializeData || {};
        const updatedUrl = updateChatbotUrl(parentUrl, {
          allow_channel_shift: null,
        });
        setInitializeData((prev: any) => ({
          ...prev,
          parentUrl: updatedUrl,
        }));
        if (IndependentChatBotId && parentConvId) {
          replaceUrlInHistory(updatedUrl);
        }
      }
      if (metadata?.conversation_id) {
        setConvId(metadata?.conversation_id);
        if (!parentConvId) {
          setLocalStorageItem({
            botId: config?.botId,
            filterId: config?.filterId,
            key: "convId",
            value: metadata?.conversation_id || "",
          });
        }
      }

      const enableLanguageToggle = initializeData?.enableLanguageToggle;
      if (dataResp?.language && enableLanguageToggle) {
        handleLanguageChange({
          newLanguage: dataResp?.language || "",
          initializeData: initializeData,
        });
      }

      setChatInputConfigs((prev) => {
        const newConfigs = { ...prev, value: "" };

        if (dataResp?.hasOwnProperty("allow_free_text")) {
          newConfigs.disableInput = !dataResp.allow_free_text;
        }

        if (Array.isArray(dataResp?.option_phrases)) {
          newConfigs.placeholder = dataResp.option_phrases;
        }

        return newConfigs;
      });

      if (dataResp?.error?.restart_conversation) {
        setChatInputConfigs((prev) => ({
          ...prev,
          disableInput: true,
          value: "",
        }));
        setTimeout(() => {
          reinitializeChat();
        }, 2000);
      }

      if (dataResp?.messages?.length > 0) {
        const clonedMessage = overrideState?.messages
          ? overrideState?.messages
          : cloneDeep(messages);

        if (newUserMsg) clonedMessage.push(newUserMsg);
        const overriddenModalConfig = overrideState?.modalConfig
          ? overrideState?.modalConfig
          : modalConfig;
        const overriddenMessages = clonedMessage;
        const { _messages, _modalConfig } = handleApiResponse(
          dataResp.messages,
          onlyMobileScreen,
          {
            modalConfig: cloneDeep(overriddenModalConfig),
            messages: overriddenMessages,
          }
        );
        setMessages(_messages);
        // setTimeout(() => {
        setModalConfig((prevState) => ({
          ...prevState,
          ..._modalConfig,
        }));
        // }, 1400);
      } else {
        // don't do anything if no messages are found
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }
      const axiosError = error as AxiosError;
      const errorResponse = axiosError?.response?.status;
      if (errorResponse === STATUS_ERROR_CODE) {
        // console.log(
        //   "error found, make history call chatInputConfigs: ",
        //   chatInputConfigs
        // );
        await handleHistory({
          localConvId: convId,
          chatInputValue: overrideState?.previousInput,
        });
        return;
      } else if (errorResponse === STATUS_ERROR_404) {
        setModalConfig(initialModalConfig);
        setFooterModalConfig({
          open: true,
          data: {
            parentConvId,
          },
          type: footerModalConfigTypes.historyNotFound,
        });
      }
      setMessages((previous) => [
        ...previous,
        {
          role: BOT_ROLE,
          // turn_id: getTurnId(2),
          body: {
            text: t("pleaseTryAgain") || BOT_ERROR_MSG,
          },
          timestamp: new Date().toISOString(),
          type: "text",
          id: generateUniqueId(),
        },
      ]);
      setModalConfig(initialModalConfig);
    } finally {
      setBotLoading(false);
    }
  };

  useEffect(() => {
    const extractedConfig = config;
    let isInitialize = true;

    // if (!extractedConfig?.botId) { //TODO
    //   hideChatbotVisibility();
    //   return;
    // }

    if (extractedConfig !== null) {
      // setLoading(false);

      postMessageToParent("LIFECYCLE.LOADED", undefined);
    } else {
      setError("Error while extracting config");
    }
    //eslint-disable-next-line
  }, []);

  const handleAddMessage = (newMessage: any) => {
    setMessages((previous) => [...previous, ...newMessage]);
  };
  const handleFetch = async ({
    method = "GET",
    endpoint,
    body,
    headers,
  }: {
    method: string;
    endpoint: string;
    body?: any;
    headers?: any;
  }) => {
    abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    try {
      // Note: endpoint has starting with `/`
      const modifiedEndpoint =
        method === "GET" ? `/v1/bots${endpoint}` : `/v2/bots${endpoint}`;
      const response = await convAiClient({
        method,
        url: modifiedEndpoint,
        signal: abortControllerRef.current.signal,
        data: body,
        headers: {
          ...(headers ? { ...headers } : {}),

          js_version: initializeData?.js_version || undefined,
        },
      });

      // Handle the response as needed
      if (response?.status !== 200) {
        throw new Error("Network response was not ok");
      }
      const responseHeaders = response?.headers;
      setLatestResponseHeaders(responseHeaders);
      return response.data; // Return the data from the response
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log("Request canceled", error.message);
      } else {
        console.error("Error fetching data:", error);
        throw error; // Rethrow the error for further handling if necessary
      }
    }
  };
  const handleInitialResponse = ({
    response,
    callType = FETCH_CALL_TYPE.initial,
    chatInputValue,
    parentConvIdDirect,
    localInitializeData,
  }: {
    response: any;
    callType: string;
    chatInputValue?: string;
    parentConvIdDirect?: string;
    localInitializeData?: any;
  }) => {
    const metadata = response?.metadata;
    const dataResp = response?.response;
    if (metadata?.conversation_id) {
      setConvId(metadata?.conversation_id);
      const parentConversationId = parentConvIdDirect || parentConvId;
      // console.log("parentConversationId", parentConversationId, {
      //   parentConvIdDirect,
      //   parentConvId,
      // });
      if (!parentConversationId) {
        setLocalStorageItem({
          botId: config?.botId,
          filterId: config?.filterId,
          key: "convId",
          value: metadata?.conversation_id || "",
        });
      }
    }

    setChatInputConfigs((prev) => {
      const newConfigs = {
        ...prev,
        value: chatInputValue ? chatInputValue : "",
      };

      if (dataResp?.hasOwnProperty("allow_free_text")) {
        newConfigs.disableInput = !dataResp.allow_free_text;
      }

      if (Array.isArray(dataResp?.option_phrases)) {
        newConfigs.placeholder = dataResp.option_phrases;
      }

      return newConfigs;
    });
    const _initializeData = localInitializeData ?? initializeData;
    const enableLanguageToggle = _initializeData?.enableLanguageToggle;
    if (dataResp?.language && enableLanguageToggle) {
      handleLanguageChange({
        newLanguage: dataResp?.language || "",
        initializeData: _initializeData,
      });
    }

    if (dataResp?.messages?.length > 0) {
      const dataRespMessagesFiltered = dataResp?.messages?.filter(Boolean);

      const { _messages, _modalConfig } = handleApiResponse(
        dataRespMessagesFiltered,
        onlyMobileScreen,
        {
          modalConfig: cloneDeep(initialModalConfig),
          messages: [], //here passing [] as we are sure previuos msg only text
        }
      );
      setMessages((previous) => {
        if (callType === FETCH_CALL_TYPE.initial) {
          return [...previous, ..._messages];
        } else {
          return [..._messages];
        }
      });
      setModalConfig((prevState) => ({
        ...prevState,
        ..._modalConfig,
      }));
    } else throw new Error("no msg found");
  };
  const handleErrorMessage = () => {
    setMessages((previous) => [
      ...previous,
      {
        role: BOT_ROLE,
        // turn_id: "1",
        type: "text",
        timestamp: new Date().toISOString(),
        body: {
          text: t("pleaseTryAgain") || BOT_ERROR_MSG,
        },
        id: generateUniqueId(),
      },
    ]);
    setModalConfig(initialModalConfig);
  };
  const fetchInitialMsg = async ({
    method = "GET",
    endpoint,
    body,
    headers,
  }: {
    method: string;
    endpoint: string;
    body?: any;
    headers?: any;
  }) => {
    setBotLoading(true);
    try {
      const response = await handleFetch({
        method,
        endpoint,
        body,
        headers,
      });
      return response;
    } catch (error) {
      if (axios.isCancel(error)) return;
      console.log(" error", error);
      handleErrorMessage();
      throw error;
    } finally {
    }
  };

  const handleFormSubmit = async (data: any, type?: string) => {
    setBotLoading(true);
    const {
      jobId = "",
      candidateId: candidateIdOrNull = "a7e8c70730bf57b79110c73f2863df69ea",
      applicationId = "",
      templateId = "",
      id = "",
      screensStatus,
    } = data || {};
    const candidateId =
      candidateIdOrNull ?? "a7e8c70730bf57b79110c73f2863df69ea";
    const applySkill = modalConfig?.data?.action_payload?.skill || "";
    const applyState = modalConfig?.data?.action_payload?.state || "";
    setModalConfig(initialModalConfig);
    try {
      const payload = {
        body: null,
        action: {
          type: "text",
          action_type: "action",
          payload: {
            job_id: jobId,
            submission_id: applicationId,
            candidate_id: candidateId,
            form_id: id,
            skill: applySkill,
            screening_result: screensStatus ?? true,
            state: applyState,
            error: type === "formSubmitError" ? true : false,
          },
        },
      };
      await fetchBotResponse(payload, {
        modalConfig: initialModalConfig,
      });
    } catch (error) {
      // console.log("form submit error", error);
    } finally {
      setBotLoading(false);
    }
  };

  const handleInterviewSchedule = async () => {
    setBotLoading(true);
    try {
      const payload = {
        body: null,
        action: {
          type: "text",
          action_type: "action",
          payload: {
            ...schedulerMsg?.data,
            success: true,
          },
        },
      };

      await fetchBotResponse(payload, {
        modalConfig: initialModalConfig,
      });
    } catch (error) {
      console.log("interview submit error", error);
    } finally {
      setBotLoading(false);
    }
  };
  const handleInterviewScheduleMsgId = async (data?: any) => {
    const { status, ...restData } = data || {};
    setBotLoading(true);
    const lastMsg = messages[messages?.length - 1];
    let msgId = parentMessageId;
    const botId = config?.botId;
    if (lastMsg?.type === ResponseType.Scheduling) {
      msgId = lastMsg?.id;
    }

    try {
      const payload = {
        ...schedulerMsg?.data,
        ...(restData || {}),
        success: true,
        job_id: "",
      };
      const response = await convAiClient.post(
        `/v1/bots/${botId}/conversations/${convId}/messages/${msgId}/interview_message`,
        payload,
        {
          headers: {
            js_version: initializeData?.js_version || undefined,
          },
        }
      );

      if (response?.status !== 200) {
        throw new Error("Network response was not ok");
      }
      const responseHeaders = response?.headers;
      setLatestResponseHeaders(responseHeaders);
      const dataResp = response?.data?.response;
      if (dataResp?.messages?.length > 0) {
        setMessages((previous) => [...previous, ...dataResp?.messages]);
      }
    } catch (error) {
      console.log("interview submit error", error);
      setMessages((previous) => [
        ...previous,
        {
          role: BOT_ROLE,
          // turn_id: getTurnId(2),
          body: {
            text: t("pleaseTryAgain") || BOT_ERROR_MSG,
          },
          timestamp: new Date().toISOString(),
          type: "text",
          id: generateUniqueId(),
        },
      ]);
    } finally {
      setBotLoading(false);
    }
  };

  const fetchLatestChat = () => {
    // tab change
    // setBotLoading(true);
    // if (parentConvId) {
    //   handleMailLink({ conversation_id: parentConvId });
    //   return;
    // }
    const localConvId = getUniversalConvId();
    if (!localConvId) {
      abortControllerRef.current.abort();
      reinitializeChat();
    }

    if (localConvId !== convId) {
      // setBotLoading(true);
      setConvId(localConvId);
    }
  };
  const handleHistory = async ({
    localConvId,
    chatInputValue,
    parentConvId,
    localBotId,
    parentMessageId,
    localInitializeData,
  }: {
    localConvId: string;
    chatInputValue?: string;
    parentConvId?: string;
    localBotId?: string;
    parentMessageId?: string;
    localInitializeData?: any;
  }) => {
    const modifiedBotId = localBotId || config?.botId;
    if (!modifiedBotId) return;
    let endpoint = `/${modifiedBotId}/conversations/${localConvId}`;
    if (parentMessageId) {
      endpoint += `/messages/${parentMessageId}`;
    }
    const headers: any = {
      website_url: localInitializeData?.parentUrl || "",
    };
    try {
      const response = await fetchInitialMsg({
        method: "GET",
        endpoint,
        headers,
      });
      if (!response) return;
      handleInitialResponse({
        response,
        callType: FETCH_CALL_TYPE.history,
        chatInputValue,
        parentConvIdDirect: parentConvId,
        localInitializeData,
      });
      setEditState(initialEditState);
    } catch (error) {
      console.log("error", error);
      const axiosError = error as AxiosError;
      const errorResponse = axiosError?.response?.status;
      if (errorResponse === STATUS_ERROR_404) {
        setModalConfig(initialModalConfig);
        setFooterModalConfig({
          open: true,
          data: {
            parentConvId,
          },
          type: footerModalConfigTypes.historyNotFound,
        });
      } else if (errorResponse === STATUS_ERROR_500) {
        reinitializeChat();
      }
    } finally {
      setBotLoading(false);
    }
  };
  const handleInitalIndependentStarterBot = (response: any) => {
    const conversationId = response?.metadata?.conversation_id;
    if (conversationId && IndependentChatBotId) {
      setBotLoading(true);

      // Navigate first - URL becomes the source of truth
      const urlParams = new URLSearchParams(window.location.search);
      const variantId = urlParams.get("variantId") || "";

      navigate(
        `/${ROUTES.web}/${IndependentChatBotId}/${conversationId}${
          variantId ? `?variantId=${variantId}` : ""
        }`,
        { replace: true }
      );

      // Process the response after navigation using requestAnimationFrame
      requestAnimationFrame(() => {
        handleInitialResponse({
          response,
          callType: FETCH_CALL_TYPE.initial,
        });
        // Set loading to false after response is processed
        setBotLoading(false);
      });
    } else {
      setMessages((previous) => [
        ...previous,
        {
          role: BOT_ROLE,
          body: {
            text: t("pleaseTryAgain") || BOT_ERROR_MSG,
          },
          timestamp: new Date().toISOString(),
          type: "text",
          id: generateUniqueId(),
        },
      ]);
    }
  };
  const handleInitialConversation = async ({
    queryString,
    body,
    headers,
  }: {
    queryString?: string;
    body?: any;
    headers?: any;
  }) => {
    const isQueryApi = config?.isQueryApi ? `?show_demo_script=true` : "";
    const botId = config?.botId;
    if (!botId) return;
    const apiUrl = `conversations${isQueryApi}`;
    const endpoint = `/${config?.botId}/${apiUrl}${
      queryString ? "?" + queryString : ""
    }`;
    try {
      const response = await fetchInitialMsg({
        method: "POST",
        endpoint,
        body,
        headers,
      });
      if (!response) return;
      if (independentStarterBot) {
        setBotLoading(true);
        handleInitalIndependentStarterBot(response);

        return; // Exit early to prevent double execution
      }

      handleInitialResponse({
        response,
        callType: FETCH_CALL_TYPE.initial,
      });
    } catch (error) {
      console.log("error", error);
    } finally {
      setBotLoading(false);
    }
  };

  const handleInitialize = async (initialData: any) => {
    const currentLanguage = initialData?.config?.current_language || "en-US";

    // Remove duplicate starter message generation - let TranslationContext handle it
    const isAllowFreeText = !(initialData?.consentType === CONSENT_TYPE.ACTIVE);
    setBotLoading(true);
    setChatInputConfigs((prev) => {
      const newConfigs = { ...prev, value: "" };
      newConfigs.disableInput = !isAllowFreeText;
      return newConfigs;
    });

    setInitializeData((prev: any) => ({
      ...prev,
      ...initialData,
    }));

    const starterMsg = initializeTranslations(initialData) || [];

    //here don't use getUniversalConvId as based on seperate , handle seperate
    if (parentConvId && isIndependentBot) {
      const _msgId = initialData?.messageId;
      const messageId = _msgId ?? parentMessageId;
      handleIndependentChat({
        conversation_id: parentConvId,
        botId: initialData?.botId,
        message_id: messageId,
        localInitializeData: initialData,
      });
      return;
    }

    const parentQuery = getParentUrlQuery(initialData?.parentUrl);
    const parentWebsiteConvId = parentQuery?.conversation_id;
    if (parentWebsiteConvId) {
      handleMailLink({
        conversation_id: parentWebsiteConvId,
        localInitializeData: initialData,
      });
      return;
    }

    // console.log("handleInitialize going for localConvId");
    const localConvId = getLocalStorageItem({
      botId: config?.botId,
      filterId: config?.filterId,
      key: "convId",
    });
    if (localConvId) {
      handleHistory({ localConvId, localInitializeData: initialData });
    } else {
      setMessages(starterMsg || []); // Use starter messages from TranslationContext
      if (["passive", "disabled"].includes(initialData?.consentType)) {
        if (independentStarterBot) {
          setBotLoading(false);
        } else {
          postMessageToParent("PARENT_WEBSITE", {
            IsInitial: false,
          });
        }
      } else {
        //active case depends on other stop loading
        setBotLoading(false);
      }
    }
  };
  const handleParentWebsiteEvent = (data: any) => {
    const { IsInitial, parentData } = data || {};
    const filterId = initializeData?.config?.filterId;
    const websiteUrl = initializeData?.parentUrl || "";
    setInitializeData((prev: any) => ({
      ...prev,
      ...parentData,
    }));
    if (IsInitial) {
      const mode = getValidMode(initializeData?.config?.mode);
      handleInitialConversation({
        body: {
          tnc_accepted: true,
          mode: mode,
          ...(filterId ? { filter_id: filterId } : {}),
          ...(currentLanguage
            ? { langauge: getShortLanguageCode(currentLanguage) }
            : {}),
        },
        headers: {
          website_url: websiteUrl,
        },
      });
    } else {
      setBotLoading(false);
    }
  };

  const handleUpdateMessages = (
    messageId: string | string[],
    messages: any
  ) => {
    setOptionSelection(initialOptionSelection);
    setMessages((prevMessages) => {
      const updatedMessages = removeMessageById(prevMessages, messageId);

      const newMessages = [...updatedMessages, ...messages];
      return newMessages;
    });
  };
  const reinitializeChat = async () => {
    handleClearOldData();
    abortControllerRef.current.abort();
    handleInitialize(initializeData);
  };

  const handleVideoLeave = async (interviewId: string, payload: any) => {
    const botId = config?.botId;
    setBotLoading(true);

    try {
      // /conversations/{conversation_id}
      const response = await convAiClient.post(
        `/v1/bots/${botId}/conversations/${convId}/video_interview`,
        payload,
        {
          headers: {
            js_version: initializeData?.js_version || undefined,
          },
        }
      );

      if (response?.status !== 200) {
        throw new Error("Network response was not ok");
      }
      const dataResp = response?.data?.response;
      setChatInputConfigs((prev) => {
        const newConfigs = { ...prev, value: "" };

        if (dataResp?.hasOwnProperty("allow_free_text")) {
          newConfigs.disableInput = !dataResp.allow_free_text;
        }

        if (Array.isArray(dataResp?.option_phrases)) {
          newConfigs.placeholder = dataResp.option_phrases;
        }

        return newConfigs;
      });
      setMessages((prev) => [...prev, ...dataResp.messages]);
      // console.log("response", response);
    } catch (error) {
      console.log("error", error);
    } finally {
      setBotLoading(false);
    }
  };

  const handleUpdateMessage = (data: any) => {
    const { messages = [], addToExisting } = data || {};
    setMessages((prev) => {
      return addToExisting ? [...prev, ...messages] : [...messages];
    });
  };

  useEffect(() => {
    const botId = config?.botId;
    const mode = getValidMode(initializeData?.config?.mode);
    // If convId is not available, do not set up polling

    if (!convId) return;

    const controller = new AbortController(); // Create an AbortController instance
    const { signal } = controller; // Get the signal from the controller
    const isHistoryNotFound =
      footerModalConfig?.type === footerModalConfigTypes.historyNotFound;
    const interval = setInterval(async () => {
      try {
        if (
          botLoading ||
          isHistoryNotFound ||
          !lastSystemMsgId ||
          parentMessageId ||
          (activeView !== "side" && !isIndependentBot) ||
          lastMsgRole !== BOT_ROLE ||
          mode === CHAT_MODE.draft ||
          editState?.isEditing
        ) {
          controller.abort();
          return;
        }

        const endpoint = `/v1/bots/${botId}/conversations/${convId}/should_refresh?latest_message_id=${lastSystemMsgId}`;
        const response = await convAiClient.get(endpoint, {
          signal,
          headers: {
            website_url: initializeData?.parentUrl || "",

            js_version: initializeData?.js_version || undefined,
          },
        }); // Pass the signal to the request
        // console.log("polling response", response);
        if (response.data.refresh) {
          handleHistory({ localConvId: convId }); // Set refresh state to true
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          // Do not log if the request was canceled
          return;
        } else {
          console.error("Error:", error);
        }
      }
    }, 5000); // Poll every 5 seconds

    return () => {
      clearInterval(interval);
      controller.abort();
    };
  }, [
    convId,
    lastSystemMsgId,
    botLoading,
    config?.botId,
    activeView,
    lastMsgRole,
  ]);

  const cancelEditMessage = () => {
    if (!editState.isEditing || !editState.originalMessage) return;

    // Find the current position of the original message in the messages array
    // Since we're not relying on IDs, we need to find it by content
    const currentMessageIndex = messages.findIndex(
      (msg) =>
        msg.role === editState.originalMessage?.role &&
        msg.body?.text === editState.originalMessage?.body?.text &&
        msg.timestamp === editState.originalMessage?.timestamp
    );

    if (currentMessageIndex === -1) {
      // If we can't find the message, just restore from stored data
      setMessages([...messages, ...editState.messagesAfterEdit]);
    } else {
      // Restore all messages including the ones that were after the edited message
      const messagesBeforeEdit = messages.slice(0, currentMessageIndex);
      const restoredMessages = [
        ...messagesBeforeEdit,
        editState.originalMessage,
        ...editState.messagesAfterEdit,
      ];
      setMessages(restoredMessages);
    }

    // Clear input
    setChatInputConfigs((prev) => ({
      ...prev,
      ...(editState.previousChatInputConfigs
        ? {
            ...editState.previousChatInputConfigs,
          }
        : { value: "" }),
    }));
    setModalConfig(initialModalConfig);
    // Clear edit state
    setEditState(initialEditState);
  };

  // Sync convId from URL params - URL as single source of truth
  useEffect(() => {
    if (IndependentChatConvId) {
      setConvId(IndependentChatConvId);
    }
  }, [IndependentChatConvId]);
  return (
    <V2ChatContext.Provider
      value={{
        messages,
        config,
        error,
        showChat,
        hideChat,
        sendData,
        activeView,
        botLoading,
        convId,
        modalConfig,
        schedulerMsg,
        logoList,
        editState,
        showWidgetButton,
        generatePayload,
        genrateUserMsg,
        fetchBotResponse,

        handleAddMessage,
        setMessages,
        setModalConfig,
        optionSelection,
        setOptionSelection,
        setSchedulerMsg,
        getTurnId,
        handleFormSubmit,
        reinitializeChat,
        handleUpdateMessages,
        handleInterviewSchedule,
        setConvId,
        chatInputConfigs,
        setChatInputConfigs,
        handleInitialize,
        handleParentWebsiteEvent,
        isPopupOpen,
        setIsPopupOpen,
        isV2Chat,
        fetchLatestChat,
        initializeData,

        setInitializeData,
        footerModalConfig,
        setFooterModalConfig,
        handleInterviewScheduleMsgId,
        parentConvId,
        setBotLoading,
        independentStarterBot,
        latestResponseHeaders,
        handleVideoLeave,
        isIndependentBot,
        handleUpdateMessage,
        cancelEditMessage,
        setEditState,
      }}
    >
      {children}
    </V2ChatContext.Provider>
  );
};
